import { AdService } from '../..'
import { IAdMobileStickyConfiguration } from '../../../../domain/conf/ads'
import { DOMEvents, DOMUtils } from '../../../../utils/dom'
import { PageType } from '../../../common/page-types'
import { AdEvents } from '../events'

export class StickyMobileAd {
    public static readonly secToMsec = 1000
    public static activeTop = false
    private static closeTimer = 0

    /**
     * Initializes the sticky mobile ad with config settings
     * @param conf The config object holding settings for the mobile sticky ad
     * @param pageType The type of the current page
     * @param count How many times the init function has been called (if page loads out of order)
     * @returns
     */
    public static init(conf: IAdMobileStickyConfiguration, pageType: PageType, count = 0) {
        // Do not init if sticky is not enabled, or not active on the page type
        if (
            !(DOMUtils.isMobile() && conf.enabled) ||
            (conf.pageTargeting !== undefined && // replace with optional chaining when TS version is updated
                conf.pageTargeting.length > 0 &&
                !conf.pageTargeting.includes(pageType))
        ) {
            return
        }

        const topAd = document.querySelector(`.${AdService.DOM.classes.topAd.join('.')}`) as HTMLElement
        const adMountElement = conf.bottom
            ? document.querySelector(`.${AdService.DOM.classes.fixedBottomAdContainer}`)
            : document.querySelector(`.${AdService.DOM.classes.header}`)

        if (adMountElement && topAd) {
            adMountElement.append(topAd)
            this.activeTop = !conf.bottom
            if (!conf.bottom) {
                topAd.classList.add(AdService.DOM.classes.stickyAd)
                topAd.classList.add(AdService.DOM.classes.headerOffset)
                topAd.setAttribute(AdService.DOM.attrs.dataScroll, AdService.DOM.vals.dataScroll)
            } else {
                const fixedBottomAddOffset = document.querySelector(
                    `#${AdService.DOM.ids.fixedBottomAddOffset}`
                ) as HTMLElement
                fixedBottomAddOffset.removeAttribute('hidden')
                fixedBottomAddOffset.style.height = `${topAd.offsetHeight}px`
            }

            if (conf.close) {
                this.closeTimer = conf.closeTimer || 0
                StickyMobileAd.enableCloseButton(topAd)
            }
            // tslint:disable-next-line: no-magic-numbers
        } else if (count < 5) {
            // required for fast connections, which may load FEM before the top ad HTML tag
            // using event listeners for page load will not work for slow connections
            setTimeout(() => {
                StickyMobileAd.init(conf, pageType, count + 1)
                // tslint:disable-next-line: no-magic-numbers
            }, 50)
        }
    }

    /**
     * Displays the ad close button, after a (possibly zero) time delay
     * @param topAd The div containing the top-of-page banner ad
     */
    public static enableCloseButton(topAd: Element) {
        const closeButton = topAd.querySelector(`.${AdService.DOM.classes.closeStickyAd}`) as HTMLElement

        if (closeButton) {
            if (this.closeTimer > 0) {
                setTimeout(() => {
                    closeButton.style.display = 'inline'
                }, this.closeTimer * this.secToMsec)
            } else {
                closeButton.style.display = 'inline'
            }
            const oldTime = Date.now()
            closeButton.addEventListener('click', () => {
                const fixedBottomAddOffset = document.querySelector(
                    `#${AdService.DOM.ids.fixedBottomAddOffset}`
                ) as HTMLElement
                topAd.remove()
                fixedBottomAddOffset.setAttribute('hidden', '')
                // let other UI elements know of removal
                document.dispatchEvent(
                    new CustomEvent(AdEvents.STICKY_AD_CLOSED, {
                        detail: {
                            position: this.activeTop ? 'top' : 'bottom',
                        },
                    })
                )
                this.sendLoginAnalytics(Date.now() - oldTime)
            })
        }
    }

    /**
     * Dispatches an mParticle event to track when users are clicking the sticky ad close button.
     * time_before_close_click tracks the time between the ad slot loading and the close button being clicked.
     * Note that this is the ad slot loading, not the ad creative loading. All times are in milliseconds.
     * show_close_button_delay is the delay between slot load and the close button being shown.
     * Total time the close button was visible is equal to time_before_close_click - show_close_button_delay.
     * user_agent tracks the user's browser and device types.
     * @param timeBeforeClick The time between ad slot loading and the close button being clicked
     */
    public static sendLoginAnalytics(timeBeforeClick: number) {
        document.dispatchEvent(
            new CustomEvent(DOMEvents.CUSTOM_EVENT, {
                detail: {
                    control_fields: {
                        mparticle: {
                            keys: {
                                show_close_button_delay: 'show_close_button_delay',
                                sticky_ad_position: 'sticky_ad_position',
                                time_before_close_click: 'time_before_close_click',
                            },
                            mp_event_type: 'Other',
                        },
                    },
                    event_name: 'ad_closed',
                    show_close_button_delay: this.closeTimer * this.secToMsec,
                    sticky_ad_position: this.activeTop ? 'top' : 'bottom',
                    time_before_close_click: timeBeforeClick,
                },
            })
        )
    }
}
